import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  DollarSign, ArrowLeft, Loader2, CheckCircle2, XCircle, Clock,
  Send, RotateCcw, Building2, Calendar, FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VendorPaymentView = () => {
  const navigate = useNavigate();
  const { payment_number } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [payment, setPayment] = useState(null);


  const loadPayment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/ap/payments/${payment_number}`);
      setPayment(response.data);
    } catch (error) {
      console.error('Error loading payment:', error);
      toast.error('Failed to load payment');
      navigate('/ap/payments');
    } finally {
      setLoading(false);
    }
  }, [payment_number, navigate]);

  useEffect(() => {
    loadPayment();
  }, [loadPayment]);

  const handlePost = async () => {
    if (!window.confirm('Are you sure you want to post this payment? This will clear the vendor open items.')) return;
    
    try {
      setActionLoading(true);
      await axios.post(`${BACKEND_URL}/api/ap/payments/${payment_number}/post?posted_by=ADMIN`);
      toast.success('Payment posted successfully!');
      loadPayment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post payment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReverse = async () => {
    const reason = window.prompt('Enter reversal reason:');
    if (reason === null) return;
    
    try {
      setActionLoading(true);
      await axios.post(`${BACKEND_URL}/api/ap/payments/${payment_number}/reverse?reason=${encodeURIComponent(reason)}&reversed_by=ADMIN`);
      toast.success('Payment reversed successfully!');
      loadPayment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse payment');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-gray-100 text-gray-800 border-gray-200',
      'APPROVED': 'bg-orange-100 text-orange-800 border-orange-200',
      'POSTED': 'bg-green-100 text-green-800 border-green-200',
      'COMPLETED': 'bg-green-100 text-green-800 border-green-200',
      'REVERSED': 'bg-red-100 text-red-800 border-red-200',
      'CANCELLED': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT': return <Clock className="w-5 h-5 text-gray-600" />;
      case 'POSTED': return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'COMPLETED': return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'REVERSED': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading payment...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!payment) return null;

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="vendor-payment-view">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/ap/payments')}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <DollarSign className="w-7 h-7 text-emerald-600" />
                Vendor Payment: {payment.payment_number}
              </h1>
              <p className="text-sm text-gray-500">
                {payment.vendor_id} - {payment.vendor_name}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {payment.status === 'DRAFT' && (
              <Button onClick={handlePost} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
                <Send className="w-4 h-4 mr-2" />Post Payment
              </Button>
            )}
            {payment.status === 'POSTED' && (
              <Button variant="outline" onClick={handleReverse} disabled={actionLoading} className="text-red-600">
                <RotateCcw className="w-4 h-4 mr-2" />Reverse
              </Button>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-3 mb-6">
          <span className={`px-4 py-2 rounded-full text-sm font-medium border flex items-center gap-2 ${getStatusColor(payment.status)}`}>
            {getStatusIcon(payment.status)}
            Status: {payment.status}
          </span>
          {payment.fi_document && (
            <span className="px-4 py-2 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
              FI Document: {payment.fi_document}
            </span>
          )}
        </div>

        {/* Summary Card */}
        <div className="bg-gradient-to-r from-emerald-50 to-orange-50 rounded-lg border border-emerald-200 p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-emerald-600 flex items-center gap-1"><Building2 className="w-3 h-3" />Company Code</p>
              <p className="font-semibold text-emerald-900">{payment.company_code}</p>
            </div>
            <div>
              <p className="text-xs text-emerald-600 flex items-center gap-1"><Calendar className="w-3 h-3" />Payment Date</p>
              <p className="font-semibold text-emerald-900">{payment.payment_date}</p>
            </div>
            <div>
              <p className="text-xs text-emerald-600">Payment Method</p>
              <p className="font-semibold text-emerald-900">{payment.payment_method_name || payment.payment_method}</p>
            </div>
            <div>
              <p className="text-xs text-emerald-600">Currency</p>
              <p className="font-semibold text-emerald-900">{payment.currency}</p>
            </div>
            <div>
              <p className="text-xs text-emerald-600">Bank Account</p>
              <p className="font-semibold text-emerald-900">{payment.bank_account || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-emerald-600">Reference</p>
              <p className="font-semibold text-emerald-900">{payment.payment_reference || '-'}</p>
            </div>
          </div>
        </div>

        {/* Amount Summary */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Payment Summary
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <p className="text-xs text-orange-600">Invoice Amount</p>
              <p className="text-xl font-bold text-orange-900">{formatCurrency(payment.total_invoice_amount, payment.currency)}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-xs text-green-600">Discount Taken</p>
              <p className="text-xl font-bold text-green-900">-{formatCurrency(payment.total_discount_taken, payment.currency)}</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <p className="text-xs text-orange-600">Withholding Tax</p>
              <p className="text-xl font-bold text-orange-900">-{formatCurrency(payment.total_withholding_tax, payment.currency)}</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-lg text-center">
              <p className="text-xs text-emerald-600">Net Payment</p>
              <p className="text-xl font-bold text-emerald-900">{formatCurrency(payment.net_payment_amount, payment.currency)}</p>
            </div>
          </div>
        </div>

        {/* Allocations */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            Invoice Allocations
          </h2>
          
          {payment.allocations?.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No allocations</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice Document</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fiscal Year</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Allocated Amount</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discount Taken</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Withholding Tax</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payment.allocations?.map((alloc, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-emerald-600">{alloc.invoice_document}</td>
                      <td className="px-4 py-3 text-gray-600">{alloc.fiscal_year}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(alloc.allocated_amount, payment.currency)}</td>
                      <td className="px-4 py-3 text-right text-green-600">
                        {alloc.discount_taken > 0 ? formatCurrency(alloc.discount_taken, payment.currency) : '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-orange-600">
                        {alloc.withholding_tax > 0 ? formatCurrency(alloc.withholding_tax, payment.currency) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Audit Information */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Audit Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Created By</p>
              <p className="font-medium text-gray-900">{payment.created_by || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Created At</p>
              <p className="font-medium text-gray-900">{payment.created_at ? new Date(payment.created_at).toLocaleString() : '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Posted By</p>
              <p className="font-medium text-gray-900">{payment.posted_by || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Posted At</p>
              <p className="font-medium text-gray-900">{payment.posted_at ? new Date(payment.posted_at).toLocaleString() : '-'}</p>
            </div>
            {payment.reversed_by && (
              <>
                <div>
                  <p className="text-gray-500">Reversed By</p>
                  <p className="font-medium text-red-600">{payment.reversed_by}</p>
                </div>
                <div>
                  <p className="text-gray-500">Reversal Reason</p>
                  <p className="font-medium text-red-600">{payment.reversal_reason || '-'}</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Notes */}
        {payment.text && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Notes</h2>
            <p className="text-gray-700">{payment.text}</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VendorPaymentView;
